import { RoomAudioRenderer } from '@livekit/components-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLiveVideoSetup } from '@rsos/live-video';
import { notifyError } from '@rsos/utils/toastUtils';
import {
  StyledDispatcherLiveVideoWrapper,
  StyledVideoPlaceholder,
  StyledVideoTrack,
} from './DispatcherLiveVideo.styles';
import { DispatcherVideoContextProvider } from './DispatcherVideoContext/DispatcherVideoContext';
import { useDispatcherVideoContext } from './DispatcherVideoContext/useDispatcherVideoContext';
import { HUD } from './HUD/HUD';
import { useFullScreen } from './useFullScreen';

const DispatcherLiveVideoContent = ({
  callID,
  orgName,
  handleCardClosing,
  handleCallEnding,
}: {
  callID: string;
  orgName: string;
  handleCardClosing: () => void;
  handleCallEnding: () => void;
}) => {
  const { getDispatcherToken, endRoom } = useLiveVideoSetup({
    callID,
    orgName,
  });
  useJoinOnMountLeaveOnDismount({ getDispatcherToken });

  const { room } = useDispatcherVideoContext();
  const endRoomAndHandleCallEnding = () => {
    endRoom(room.name).catch(error => {
      // eslint-disable-next-line no-console
      console.error('Error when ending the room:', error);
    });
    handleCallEnding();
  };

  const { isFullscreen } = useFullScreen();

  const videoContent = (
    <StyledDispatcherLiveVideoWrapper
      data-name="dispatcher-live-video-wrapper"
      isFullscreen={isFullscreen}
    >
      <VideoView />
      <HUD
        handleCardClosing={handleCardClosing}
        handleCallEnding={endRoomAndHandleCallEnding}
      />
    </StyledDispatcherLiveVideoWrapper>
  );

  // in fullscreen mode, use a react portal to document.body to get around the stacking contexts created by call queue and top UNITE toolbar:
  if (isFullscreen) {
    return createPortal(videoContent, document.body);
  }

  // not fullscreen, render in place
  return videoContent;
};

function VideoView() {
  const { isBlurred, callerVideoTrack } = useDispatcherVideoContext();

  // livekit uses "muted" to describe a video track that is published but with
  // the camera turned off.
  if (!callerVideoTrack || callerVideoTrack.publication?.track?.isMuted) {
    return <StyledVideoPlaceholder />;
  }

  return (
    <>
      <RoomAudioRenderer />
      <StyledVideoTrack trackRef={callerVideoTrack} isBlurred={isBlurred} />
    </>
  );
}

function useJoinOnMountLeaveOnDismount({
  getDispatcherToken,
}: {
  getDispatcherToken: () => Promise<string>;
}) {
  const { joinRoom, leaveRoom } = useDispatcherVideoContext();
  useEffect(() => {
    getDispatcherToken()
      .then((token: string) => joinRoom(token))
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error when joining the room:', error);
        notifyError('Something went wrong when connecting');
      });

    return () => {
      leaveRoom().catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error when leaving the room:', error);
      });
    };
    // We genuinely just want to join only when this component is mounted and
    // leave only when it's unmounted. Empty dependencies is how that's done.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// Provide the DispatcherLiveVideoContext and pass on the props
export const DispatcherLiveVideo = (props: {
  callID: string;
  orgName: string;
  handleCardClosing: () => void;
  handleCallEnding: () => void;
}) => {
  return (
    <DispatcherVideoContextProvider callID={props.callID}>
      <DispatcherLiveVideoContent {...props} />
    </DispatcherVideoContextProvider>
  );
};
